// What the person manages about the organisation's connectors, and how many address requests wait for them (2026-11-02,
// Codex Astra's plan docs/superpowers/plans/2026-11-02-connectors-header-astra.md): one summary the Connectors page's
// settings and the sidebar's count share. Read on load, when the tab comes back, after a decision, and every minute while
// the tab is visible; a failed read is never shown as nothing waiting.
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getConnectorManagement, type ConnectorManagement } from "@/api/connectors";
import { useApi } from "@/composables/useApi";

const EVERY_MS = 60_000;

export const useConnectorManagementStore = defineStore("connectorManagement", () => {
  const summary = ref<ConnectorManagement | null>(null);
  const failed = ref(false);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let started = false;

  async function refresh() {
    const { client } = useApi();
    try {
      summary.value = await getConnectorManagement(client);
      failed.value = false;
    } catch {
      failed.value = true;   // the last summary stays; the count isn't shown as zero
    }
  }
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") await refresh();
      schedule();
    }, EVERY_MS);
  }
  function start() {
    if (started) return;
    started = true;
    void refresh();
    schedule();
    if (typeof document !== "undefined")
      document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void refresh(); });
  }
  function reset() {   // a different person signed in: nothing of the last one's is kept
    summary.value = null;
    failed.value = false;
  }
  /** The waiting requests an organisation admin decides (the sidebar's count), or null when there's nothing to say. */
  const toDecide = computed(() => summary.value?.addresses?.can_manage && summary.value.addresses.pending_requests > 0
    ? summary.value.addresses.pending_requests : null);
  return { summary, failed, refresh, start, reset, toDecide };
});
